import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import "./ClientEditCard.css";
import {
  Button,
  FormControl,
  MenuItem,
  Select,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from "@mui/material";
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import axios from "axios";
import { API_URL } from "../../utils/auth";
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import Auth from '../../utils/auth';
import EmailManagementModal from './EmailManagementModal';

dayjs.extend(utc);

const ClientEditCard = ({ onSave, onClose }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { clientId, clientData } = location.state || {};
  const workspaceId = Auth.getWorkspaceId();
  
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    icp: "",
    touchpointFrequency: "",
    email: "",
    phoneNumber: "",
    office: "",
    address: "",
    onboardingDate: null,
    netPromoterScore: 0,  
    clientEffortScores: 0, 
    billingStatus: "",
    monthlyFeeSubscription: 0,
    oneTimeOnboardingFee: 0,
    companyId: "",
    archived: "",
    lastContacted: "",
  });
  
  const [isSending, setIsSending] = useState(false);
  const [companies, setCompanies] = useState([]); 
  const [additionalEmails, setAdditionalEmails] = useState([]);
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
  const [isNewCompanyOpen, setIsNewCompanyOpen] = useState(false);
  const [newCompanyData, setNewCompanyData] = useState({ name: '' });
  const isDataAvailable = !!location.state && !!clientData;

  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        const response = await axios.get(`${API_URL}/utils/get-companies?workspaceId=${workspaceId}`);
        setCompanies(response.data); 
      } catch (error) {
        console.error("Error fetching companies:", error);
        toast.error("Error loading companies.");
      }
    };

    if (workspaceId) {
      fetchCompanies();
    }
  }, [workspaceId]);

  useEffect(() => {
    const fetchAdditionalEmails = async () => {
      if (!workspaceId || !clientId) return;
      
      try {
        const response = await axios.get(
          `${API_URL}/clients/client-email?workspaceId=${workspaceId}&clientId=${clientId}`
        );
        setAdditionalEmails(response.data || []);
      } catch (error) {
        console.error("Error fetching additional emails:", error);
      }
    };

    if (workspaceId && clientId) {
      fetchAdditionalEmails();
    }
  }, [workspaceId, clientId]);

  useEffect(() => {
    if (isDataAvailable) {
      setFormData({
        firstName: clientData?.firstName || "",
        lastName: clientData?.lastName || "",
        icp: clientData?.icp || "",
        touchpointFrequency: clientData?.touchpointFrequency || "",
        onboardingDate: clientData?.onboardingDate ? 
          dayjs(clientData.onboardingDate).local() : 
          null,
        email: clientData?.email || "",
        phoneNumber: clientData?.phoneNumber || "",
        office: clientData?.office || "",
        address: clientData?.address || "",
        netPromoterScore: clientData?.netPromoterScore ?? 0, 
        clientEffortScores: clientData?.clientEffortScores ?? 0,
        billingStatus: clientData?.billingStatus || "",
        monthlyFeeSubscription: clientData?.monthlyFeeSubscription ?? 0,
        oneTimeOnboardingFee: clientData?.oneTimeOnboardingFee ?? 0,
        companyId: clientData?.companyId || "",
        archived: clientData?.archived || "",
        lastContacted: clientData?.lastContacted || ""
      });
    }
  }, [clientData, isDataAvailable]);

  const handleInputChange = (event) => {
    const { name, value } = event.target;

    if (name === 'phoneNumber') {
      if (value.length < formData.phoneNumber.length) {
        setFormData({ ...formData, [name]: value });
        return;
      }

      const cleanedValue = value.replace(/[^\d+\-()\s]/g, '');
  
      setFormData({
        ...formData,
        [name]: cleanedValue, 
      });
      return;
    }

    if (['netPromoterScore', 'clientEffortScores', 'monthlyFeeSubscription', 'oneTimeOnboardingFee'].includes(name)) {
      if (value === "" || /^\d*$/.test(value)) {
        setFormData({ 
          ...formData, 
          [name]: value === "" ? "" : Number(value) 
        });
      }
      return;
    }

    setFormData({ ...formData, [name]: value });
  };

  const handleNewCompanyInputChange = (event) => {
    const { name, value } = event.target;
    setNewCompanyData({
      ...newCompanyData,
      [name]: value,
    });
  };

  const handleNewCompanySubmit = async () => {
    if (!newCompanyData.name.trim()) {
      toast.warning('Company name is required.');
      return;
    }

    const payload = {
      workspaceId: workspaceId,
      name: newCompanyData.name.trim(),
    };

    try {
      const response = await axios.post(`${API_URL}/utils/post-companies`, payload, {
        headers: { 'Content-Type': 'application/json' },
      });

      toast.success('Company created successfully!');

      setCompanies((prevCompanies) => [...prevCompanies, response.data]);

      setIsNewCompanyOpen(false);
      setNewCompanyData({ name: '' });
    } catch (error) {
      console.error('Error creating company:', error.response?.data || error.message);
      toast.error('Error creating company.');
    }
  };

  const handleDateChange = (newValue) => {
    setFormData({ 
      ...formData, 
      onboardingDate: newValue ? dayjs(newValue).startOf('day') : null 
    });
  };

  const handleSubmit = async () => {
    if (!clientId || !formData.icp || !formData.billingStatus || !formData.touchpointFrequency) {
      toast.warning("Please fill in required fields.");
      return;
    }

    const updatedClient = {
      id: clientId,
      workspaceId: workspaceId,
      companyId: formData.companyId || clientData?.companyId || "",
      firstName: formData.firstName,
      lastName: formData.lastName,
      clientRetentionScore: clientData?.clientRetentionScore || 0,
      icp: formData.icp,
      touchpointFrequency: formData.touchpointFrequency,
      onboardingDate: formData.onboardingDate ? 
        formData.onboardingDate.toISOString() : 
        dayjs().startOf('day').toISOString(),
      phoneNumber: formData.phoneNumber,
      email: formData.email,
      address: formData.address,
      office: formData.office,
      netPromoterScore: formData.netPromoterScore === "" ? null : Number(formData.netPromoterScore),
      clientEffortScores: formData.clientEffortScores === "" ? null : Number(formData.clientEffortScores),
      monthlyFeeSubscription: formData.monthlyFeeSubscription === "" ? null : Number(formData.monthlyFeeSubscription),
      oneTimeOnboardingFee: formData.oneTimeOnboardingFee === "" ? null : Number(formData.oneTimeOnboardingFee),
      billingStatus: formData.billingStatus,
      archived: clientData?.archived || false,
      lastContacted: clientData?.lastContacted || null,
    };

    setIsSending(true);
    try {
      const response = await axios.put(
        `${API_URL}/clients/clients?workspaceId=${workspaceId}`,
        updatedClient,
        {
          headers: { 
            "Content-Type": "application/json"
          },
        }
      );
      
      toast.success("Client updated successfully!");
      navigate(`/client/${clientId}`);
      onSave(updatedClient);
      onClose();
    } catch (error) {
      console.error("Error completo:", {
        message: error.message,
        response: error.response?.data,
        request: error.request
      });
      toast.error(error.response?.data?.message || "Error updating client");
    } finally {
      setIsSending(false);
    }
  };

  if (!isDataAvailable) {
    return <div>Error: Customer data not available</div>;
  }
  
  return (
    <div className={`client-edit-card ${isDataAvailable ? "open" : ""}`}>
      <div style={{ display: "flex", width: "100%", justifyContent: "space-between" }}>
        <h1>Edit Client</h1>
      </div>
      <div className="form-columns">
        {/* Left Column */}
        <div className="form-column">
          <div>
            <h2>First Name</h2>
            <input
              type="text"
              name="firstName"
              value={formData.firstName}
              onChange={handleInputChange}
              placeholder="Enter First Name"
              className="form-input"
            />
          </div>
          <div>
            <h2>Email</h2>
            <div className="email-input-container">
              <input
                type="text"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="Enter Email"
                className="form-input email-main-input"
              />
              <button 
                type="button"
                className="add-email-btn"
                onClick={() => setIsEmailModalOpen(true)}
                title="Manage additional emails"
              >
                +
              </button>
            </div>
          </div>
          <div>
            <h2>Company Name</h2>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <FormControl 
                fullWidth
                className="form-input-select"
              >
                <Select
                  labelId="company-select-label"
                  name="companyId"
                  value={formData.companyId || ""}
                  onChange={handleInputChange}
                  displayEmpty
                  sx={{
                    borderRadius: '6.7px',
                    backgroundColor: 'rgba(246, 244, 255, 1)',
                    width: '100%',
                    height: '60px',
                    marginBottom: '20px',
                    display: 'flex',
                    alignItems: 'center',
                    '& .MuiOutlinedInput-notchedOutline': {
                      border: 'none',
                    },
                    '& .MuiSelect-select': {
                      padding: '0 10px',
                      paddingRight: '40px !important',
                      fontFamily: "'Red Hat Text', sans-serif",
                      fontSize: '18px',
                      color: '#291B3B',
                      display: 'flex',
                      alignItems: 'center',
                      height: '60px',
                      lineHeight: '60px',
                    },
                    '& .MuiSelect-icon': {
                      right: '10px',
                      top: 'calc(50% - 12px)',
                    },
                  }}
                >
                  <MenuItem value="" disabled>
                    Select Company
                  </MenuItem>
                  {companies.map((company) => (
                    <MenuItem key={company.id} value={company.id}>
                      {company.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button
                variant="contained"
                style={{ 
                  marginLeft: '10px', 
                  marginBottom: '20px',
                  height: '60px',
                  backgroundColor: 'rgba(87, 42, 241, 1)',
                  color: 'white',
                  borderRadius: '6.7px',
                  textTransform: 'capitalize',
                  fontFamily: "'Red Hat Text', sans-serif",
                  fontSize: '16px',
                  fontWeight: '500'
                }}
                onClick={() => setIsNewCompanyOpen(true)}
              >
                New Company
              </Button>
            </div>
          </div>
          <div>
            <h2>Net Promoter Score</h2>
            <input
              type="number"
              name="netPromoterScore"
              value={formData.netPromoterScore}
              onChange={handleInputChange}
              placeholder="Enter Net Promoter Score"
              className="form-input"
            />
          </div>
          <div>
            <h2>Monthly Fee/Subscription</h2>
            <input
              type="number"
              name="monthlyFeeSubscription"
              value={formData.monthlyFeeSubscription}
              onChange={handleInputChange}
              placeholder="Enter Monthly Free/Subscription"
              className="form-input"
            />
          </div>
          <div>
            <h2>ICP Status</h2>
            <FormControl 
              fullWidth
              className="form-input-select"
            >
              <Select
                name="icp"
                value={formData.icp}
                onChange={handleInputChange}
                sx={{
                  borderRadius: '6.7px',
                  backgroundColor: 'rgba(246, 244, 255, 1)',
                  width: '100%',
                  height: '60px',
                  marginBottom: '20px',
                  display: 'flex',
                  alignItems: 'center',
                  '& .MuiOutlinedInput-notchedOutline': {
                    border: 'none',
                  },
                  '& .MuiSelect-select': {
                    padding: '0 10px',
                    paddingRight: '40px !important',
                    fontFamily: "'Red Hat Text', sans-serif",
                    fontSize: '18px',
                    color: '#291B3B',
                    display: 'flex',
                    alignItems: 'center',
                    height: '60px',
                    lineHeight: '60px',
                  },
                  '& .MuiSelect-icon': {
                    right: '10px',
                    top: 'calc(50% - 12px)',
                  },
                }}
              >
                <MenuItem value="ICP I">ICP I</MenuItem>
                <MenuItem value="ICP II">ICP II</MenuItem>
                <MenuItem value="ICP III">ICP III</MenuItem>
              </Select>
            </FormControl>
          </div>
          <div>
            <h2 style={{ marginBottom: '5px' }}>Onboarding Date</h2>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
            <div className="form-input-select" style={{ padding: 0, margin: 0 }}>
            <DatePicker
              value={formData.onboardingDate ? dayjs(formData.onboardingDate).local() : null}
              onChange={handleDateChange}
              format="MMM DD, YYYY"
              sx={{
                width: '100%',
                height: '60px',
                marginBottom: '20px',
                borderRadius: '6.7px',
                backgroundColor: 'rgba(246, 244, 255, 1)',
                '& .MuiOutlinedInput-root': {
                  width: '100%',
                  height: '60px',
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  border: 'none',
                },
                '& .MuiInputBase-input': {
                  padding: '10px 14px',
                  fontFamily: "'Red Hat Text', sans-serif",
                  fontSize: '18px',
                  color: '#291B3B',
                },
              }}
            />
            </div>
          </LocalizationProvider>
        </div>
        </div>
        {/* Right Column */}
        <div className="form-column">
          <div>
            <h2>Last Name</h2>
            <input
              type="text"
              name="lastName"
              value={formData.lastName}
              onChange={handleInputChange}
              placeholder="Enter Last Name"
              className="form-input"
            />
          </div>
          <div>
            <h2>Phone Number</h2>
            <input
              type="text"
              name="phoneNumber"
              value={formData.phoneNumber}
              onChange={handleInputChange}
              placeholder="Enter Phone Number"
              className="form-input"
            />
          </div>
          <div>
            <h2>Address</h2>
            <input
              type="text"
              name="address"
              value={formData.address}
              onChange={handleInputChange}
              placeholder="Enter Address"
              className="form-input"
            />
          </div>
          <div>
            <h2>Client Effort Score</h2>
            <input
              type="number"
              name="clientEffortScores"
              value={formData.clientEffortScores}
              onChange={handleInputChange}
              placeholder="Enter Client Effort Score"
              className="form-input"
            />
          </div>
          <div>
            <h2>One-Time Onboarding Fee</h2>
            <input
              type="number"
              name="oneTimeOnboardingFee"
              value={formData.oneTimeOnboardingFee}
              onChange={handleInputChange}
              placeholder="Enter One-Time Onboarding Fee"
              className="form-input"
            />
          </div>
          <div>
            <h2>Touchpoint Frequency</h2>
            <FormControl 
              fullWidth
              className="form-input-select"
            >
              <Select
                name="touchpointFrequency"
                value={formData.touchpointFrequency}
                onChange={handleInputChange}
                sx={{
                  borderRadius: '6.7px',
                  backgroundColor: 'rgba(246, 244, 255, 1)',
                  width: '100%',
                  height: '60px',
                  marginBottom: '20px',
                  display: 'flex',
                  alignItems: 'center',
                  '& .MuiOutlinedInput-notchedOutline': {
                    border: 'none',
                  },
                  '& .MuiSelect-select': {
                    padding: '0 10px',
                    paddingRight: '40px !important',
                    fontFamily: "'Red Hat Text', sans-serif",
                    fontSize: '18px',
                    color: '#291B3B',
                    display: 'flex',
                    alignItems: 'center',
                    height: '60px',
                    lineHeight: '60px',
                  },
                  '& .MuiSelect-icon': {
                    right: '10px',
                    top: 'calc(50% - 12px)',
                  },
                }}
              >
                <MenuItem value="Weekly">Weekly</MenuItem>
                <MenuItem value="Bi-Weekly">Bi-Weekly</MenuItem>
                <MenuItem value="Monthly">Monthly</MenuItem>
              </Select>
            </FormControl>
          </div>
          <div>
            <h2>Billing Status</h2>
            <FormControl 
              fullWidth
              className="form-input-select"
            >
              <Select
                name="billingStatus"
                value={formData.billingStatus}
                onChange={handleInputChange}
                sx={{
                  borderRadius: '6.7px',
                  backgroundColor: 'rgba(246, 244, 255, 1)',
                  width: '100%',
                  height: '60px',
                  marginBottom: '20px',
                  display: 'flex',
                  alignItems: 'center',
                  '& .MuiOutlinedInput-notchedOutline': {
                    border: 'none',
                  },
                  '& .MuiSelect-select': {
                    padding: '0 10px',
                    paddingRight: '40px !important',
                    fontFamily: "'Red Hat Text', sans-serif",
                    fontSize: '18px',
                    color: '#291B3B',
                    display: 'flex',
                    alignItems: 'center',
                    height: '60px',
                    lineHeight: '60px',
                  },
                  '& .MuiSelect-icon': {
                    right: '10px',
                    top: 'calc(50% - 12px)',
                  },
                }}
              >
                <MenuItem value="Active">Active</MenuItem>
                <MenuItem value="Paused">Paused</MenuItem>
                <MenuItem value="Past Due">Past Due</MenuItem>
              </Select>
            </FormControl>
          </div>
        </div>
      </div>
      <div className="form-actions">
        <Button
          className="submit-button"
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          disabled={isSending}
        >
          {isSending ? "Updating..." : "Update"}
        </Button>
      </div>
      <Dialog
        open={isNewCompanyOpen}
        onClose={() => setIsNewCompanyOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Create New Company</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            name="name"
            label="Company Name"
            type="text"
            fullWidth
            variant="outlined"
            value={newCompanyData.name}
            onChange={handleNewCompanyInputChange}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsNewCompanyOpen(false)}>Cancel</Button>
          <Button onClick={handleNewCompanySubmit} variant="contained">
            Create
          </Button>
        </DialogActions>
      </Dialog>
      <EmailManagementModal
        open={isEmailModalOpen}
        onClose={() => setIsEmailModalOpen(false)}
        clientId={clientId}
        workspaceId={workspaceId}
        additionalEmails={additionalEmails}
        setAdditionalEmails={setAdditionalEmails}
      />
    </div>
  );
};

export default ClientEditCard;